import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Divider, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Link, RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function ForgotPassword() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const domain = useSelector((state: RootState) => state.user.domain);
  const { e } = useLocalSearchParams<{ e: string }>();

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      email: e,
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchUnauthorized(
        "ForgotPassword.onSubmit",
        "/rabbl/api/forgotpassword",
        "POST",
        {
          domain: domain,
          email: data.email,
        }
      );

      setError('Instructions sent. Check your email.');

    } catch (e : any) {
      AppUtilities.handleException(e, "ForgotPassword.onSubmit");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Forgot password"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Get a link to reset your password (check your spam/junk/promotions folders):</AppText>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                ref={emailInput}
                value={value}
                onChangeText={onChange}
                placeholder="Email"
                style={styles.formElement}
                returnKeyType="next"
                onBlur={onBlur}
                autoComplete="email"
                keyboardType="email-address"
                textContentType="emailAddress"
                autoCapitalize="none"
                onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
              />
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Email is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Reset password</Button95>
          </View>

          { (error && error.length > 0) ?
                (
                  <>
                    <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>{error}</AppText>
                  </>
                )
              :
                null
          }
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
